// ActivityChart — GitHub-style 52-week heatmap. Generic and reusable.
//
// Configuration via parent props (read on the element itself in v3.14):
//   data           — { 'YYYY-MM-DD': { score: number, breakdown?: object } }
//   title          — header label (default: 'ACTIVITY · LAST YEAR')
//   accent         — base color token for filled cells (default: 'green')
//   onCellClick    — (date, entry, root) handler when a non-empty cell is clicked
//   showLegend     — boolean (default: true)
//
// Reads `data` from `s.root._chartActivityData` by default if no `data` prop;
// callers control the same root key when fetching activity. Use the
// `chartId` prop to namespace multiple charts on the same page.
//
// Tickets/MyView and the new /u/:slug profile page both mount this; the
// caller is responsible for fetching data and writing it to the matching
// root key (e.g. `_activityData` for tickets, `_profileActivity` for profile).

const WEEKS = 52
const DAYS = 7

// FA204 / frank trap — helpers live in `scope`, not module scope: a bare
// module-scope const/function silently reads as undefined once the
// component is frank.toJSON'd for platform delivery (children/background
// factories then throw and the whole subtree never mounts — see workspace
// d6ea1142, the Geex primitives precedent, and
// project_frank_drops_module_local_helpers). `el.scope.<name>` resolves from
// any descendant factory since `scope:` is merged onto every instance.
export const ActivityChart = {
  scope: {
    // Total cell count — plain computed value (not a function body), so this
    // bakes to a literal number at serialization time; still safer to keep
    // off the raw WEEKS/DAYS module consts, which are ONLY safe today because
    // they're read at object-literal build time, never inside a factory.
    total: WEEKS * DAYS,
    // Resolve the data source. Data lives on either:
    //   - `el.parent.data` (a static object passed as a prop)
    //   - `s.root[el.parent.dataKey]` (a key into root state, default `_chartActivityData`)
    readData: (el, s) => {
      const owner = el.parent
      if (owner?.data && typeof owner.data === 'object') return owner.data
      const key = owner?.dataKey || '_chartActivityData'
      return s.root?.[key] || {}
    },
    shade: (accent, n) => `${accent}.${n}`
  },
  flow: 'y',
  gap: 'Z',

  CaptionTitle: {
    fontSize: 'Y2',
    // `heading` FIRST, `title` only as the back-compat fallback: `title` is a
    // global HTML attribute, so neither rewrite form works on it when a child
    // reads it as CONTENT (docs/I18N-CONVENTION.md §1.2). Both paths resolve
    // through polyglot, so a call site may pass a bundle KEY or a plain
    // string — polyglot returns a non-key verbatim.
    Text: {
      text: (el) =>
        el.call(
          'polyglot',
          el.parent.parent.heading || el.parent.parent.title || 'account.userProfile.activity.title'
        )
    }
  },

  Grid: {
    display: 'grid',
    gridTemplateColumns: `repeat(${WEEKS}, 1fr)`,
    gridTemplateRows: `repeat(${DAYS}, 1fr)`,
    gridAutoFlow: 'column',
    gap: 'X',
    padding: 'Z 0',

    childExtends: 'ActivityChartCell',
    childrenAs: 'state',
    children: (el, s) => {
      const data = el.scope.readData(el, s)
      const today = new Date()
      const cells = []
      for (let i = el.scope.total - 1; i >= 0; i--) {
        const d = new Date(today)
        d.setDate(d.getDate() - i)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        const entry = data[key] || {}
        cells.push({
          date: key,
          score: entry.score || 0,
          marker: entry.marker || entry.standups || 0,
          breakdown: entry.breakdown || {}
        })
      }
      return cells
    }
  },

  Legend: {
    tag: 'div',
    hide: (el) => el.parent.showLegend === false,
    flow: 'x',
    gap: 'Z',
    align: 'center right',
    fontSize: 'Y1',
    color: 'caption',

    Less: { tag: 'span', text: '{{ account.userProfile.activity.less | polyglot }}' },
    Box_1: {
      width: 'Y',
      height: 'Y',
      round: 'X',
      // Fill-contrast swatch (was a bordered/transparent square) — matches
      // the solid-fill siblings below it instead of standing out as the
      // only outlined legend key.
      background: 'overlay'
    },
    Box_2: {
      width: 'Y',
      height: 'Y',
      round: 'X',
      background: (el) => el.scope.shade(el.parent.parent.accent || 'green', 15)
    },
    Box_3: {
      width: 'Y',
      height: 'Y',
      round: 'X',
      background: (el) => el.scope.shade(el.parent.parent.accent || 'green', 3)
    },
    Box_4: {
      width: 'Y',
      height: 'Y',
      round: 'X',
      background: (el) => el.scope.shade(el.parent.parent.accent || 'green', 5)
    },
    Box_5: {
      width: 'Y',
      height: 'Y',
      round: 'X',
      background: (el) => el.scope.shade(el.parent.parent.accent || 'green', 7)
    },
    More: { tag: 'span', text: '{{ account.userProfile.activity.more | polyglot }}' }
  }
}

export const ActivityChartCell = {
  // FA204 / frank trap — `_intensity`'s logic lives here as a scoped
  // helper (duplicated rather than shared with ActivityChart's `shade`
  // above, since scope object properties can't reference sibling scope
  // properties as bare identifiers) — see the header comment above
  // ActivityChart for the full rationale.
  scope: {
    intensity: (score, accent = 'green', hasMarker) => {
      const shade = (n) => `${accent}.${n}`
      // Empty cells read via a neutral FILL (matches every other intensity
      // step being a fill), not a border-only outline — the border-habit
      // sweep (tickets/opus.md) replaced the old `borderColor: 'line'` ring
      // with this.
      if (!score && !hasMarker) return 'overlay'
      if (hasMarker && !score) return shade(15)
      if (score <= 2) return shade(15)
      if (score <= 5) return shade(3)
      if (score <= 10) return shade(5)
      return shade(7)
    }
  },
  width: '100%',
  aspectRatio: '1',
  round: 'X',
  background: (el, s) => {
    const accent = el.parent.parent.accent || 'green'
    return el.scope.intensity(s.score, accent, s.marker > 0)
  },
  cursor: (el, s) => {
    if (!s.score && !s.marker) return 'default'
    return el.parent.parent.onCellClick ? 'pointer' : 'default'
  },
  transition: 'background 120ms ease',
  // Was a static `borderColor: 'line'` ring on empty cells (the border
  // habit) — the cell's own `background` (via `el.scope.intensity`) now
  // carries the empty/filled distinction as a fill step instead of a stroke.
  ':hover': { outline: 'hairline solid title' },
  title: (el, s) => {
    if (!s.score && !s.marker) return s.date
    const parts = []
    if (s.marker) parts.push('✦')
    const bd = s.breakdown || {}
    for (const [k, n] of Object.entries(bd)) {
      parts.push(`${n} ${k}${n > 1 ? 's' : ''}`)
    }
    return `${s.date} · ${s.score}pt${s.score === 1 ? '' : 's'}${parts.length ? ' — ' + parts.join(', ') : ''}`
  },
  onClick: (e, el, s) => {
    if (!s.score && !s.marker) return
    const handler = el.parent.parent.onCellClick
    if (typeof handler === 'function') handler(s.date, s, s.root)
  }
}
